import { cn } from '@/utils/index';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { ChevronLeft } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button/Button';

type TabsProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> & {
  isSwitch?: boolean;
};

const Tabs = ({ isSwitch, ...props }: TabsProps) => {
  return <TabsPrimitive.Root data-switch={isSwitch ? 'true' : 'false'} {...props} className={cn('group', props.className)} />;
};

const SCROLL_BY_IN_PX = 220;

const TabsList = ({ className, ...props }: TabsPrimitive.TabsListProps) => {
  const { t } = useTranslation('components', { keyPrefix: 'tabs' });
  const listRef = useRef<HTMLDivElement | null>(null);
  const listId = useId();

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const refresh = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    // slight epsilon to avoid off-by-1 flicker
    const epsilon = 1;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - epsilon);
  }, []);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const onScroll = () => refresh();
    el.addEventListener('scroll', onScroll, { passive: true });

    const ro = new ResizeObserver(refresh);
    ro.observe(el);

    window.addEventListener('resize', refresh);

    // Initial check
    refresh();

    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
      window.removeEventListener('resize', refresh);
    };
  }, [refresh]);

  const scrollBy = (dx: number) => {
    const el = listRef.current;
    if (!el) return;
    el.scrollBy({ left: dx, behavior: 'smooth' });
  };

  return (
    <div className='relative' role='group'>
      {canScrollLeft && (
        <Button
          size='icon-l'
          variant='icon'
          className='bg-fill-background-light hover:bg-fill-background-medium absolute top-1/2 -left-1.5 z-10 -translate-y-1/2'
          aria-label={t('scrollLeft.ariaLabel')}
          aria-controls={listId}
          onClick={() => scrollBy(-SCROLL_BY_IN_PX)}
        >
          <ChevronLeft className='text-text-default-secondary size-6' />
        </Button>
      )}

      {canScrollRight && (
        <Button
          size='icon-l'
          variant='icon'
          className='bg-fill-background-light hover:bg-fill-background-medium absolute top-1/2 -right-1.5 z-10 -translate-y-1/2'
          aria-label={t('scrollRight.ariaLabel')}
          aria-controls={listId}
          onClick={() => scrollBy(SCROLL_BY_IN_PX)}
        >
          <ChevronLeft className='text-text-default-secondary size-6 rotate-180' />
        </Button>
      )}

      <TabsPrimitive.List
        id={listId}
        ref={listRef}
        className={cn(
          '-mx-1 -mt-1 inline-flex w-full items-center justify-start gap-5 pb-px',
          'border-outline-neutral border-b',
          'group-data-[switch=true]:bg-fill-background-medium group-data-[switch=true]:gap-0 group-data-[switch=true]:rounded-full group-data-[switch=true]:border-0',
          'no-scrollbar overflow-x-auto overflow-y-hidden scroll-smooth',
          className,
        )}
        {...props}
      />
    </div>
  );
};

type TabsTriggerProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
  showChanges?: boolean;
};

const TabsTrigger = ({ className, children, showChanges, ...props }: TabsTriggerProps) => {
  return (
    <TabsPrimitive.Trigger
      className={cn(
        'relative mx-1 inline-flex w-fit items-center justify-center gap-2 py-2 whitespace-nowrap',
        'text-body-m-regular text-text-default-secondary',
        'hover:text-text-default-primary',
        'focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2',
        'focus-visible:outline-hidden',
        'ring-offset-fill-background-neutral',

        // Active state
        'data-[state=active]:text-body-m-semibold',
        'data-[state=active]:text-text-brand-primary',
        'data-[state=active]:after:absolute data-[state=active]:after:bottom-0 data-[state=active]:after:left-1/2',
        'data-[state=active]:after:block data-[state=active]:after:h-[3px] data-[state=active]:after:w-full',
        'data-[state=active]:after:-translate-x-1/2 data-[state=active]:after:translate-y-1/2',
        'data-[state=active]:after:bg-text-brand-primary',

        // Disabled state
        'disabled:pointer-events-none disabled:opacity-50',

        // Switch variant styles
        'group-data-[switch=true]:text-body-m-regular group-data-[switch=true]:w-full',
        'group-data-[switch=true]:data-[state=active]:bg-text-brand-primary',
        'group-data-[switch=true]:data-[state=active]:text-text-default-inverted',
        'group-data-[switch=true]:data-[state=active]:rounded-full',
        'group-data-[switch=true]:data-[state=active]:after:content-none',
        'transition-all',
        className,
      )}
      {...props}
    >
      <div>
        <div>{children}</div>
        {/* Invisible div to prevent layout shift */}
        <div className='invisible h-0 font-semibold' aria-hidden>
          {children}
        </div>
      </div>
      {showChanges && <span className='bg-text-brand-primary size-1 self-start rounded-full' />}
    </TabsPrimitive.Trigger>
  );
};

const TabsContent = ({ className, ...props }: React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>) => (
  <TabsPrimitive.Content
    className={cn(
      'ring-offset-fill-background-neutral focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 focus-visible:outline-hidden',
      className,
    )}
    {...props}
  />
);

export { Tabs, TabsContent, TabsList, TabsTrigger };
